import { useEffect, useRef, useState, type FormEventHandler } from 'react'
import { Navigate, useLocation } from 'react-router'
import { Box } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import type { UiNode } from '@ory/client'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import InlineMessage from '@base/InlineMessage/InlineMessage.component'
import TextField from '@base/TextField/TextField.component'
import MailIcon from '@icons/Mail.icon'
import AuthError from '@components/AuthError/AuthError.component'
import AuthLoading from '@components/AuthLoading/AuthLoading.component'
import BackToLogin from '@components/BackToLogin/BackToLogin.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'
import { isLinkingMessage } from '@i18n/kratos/kratos-i18n'
import { findInput, getFlowMessages } from '@utils/kratos-nodes.utils'
import { isSameOriginFormAction } from '@utils/url.utils'

import AlreadySignedInPanel from './components/AlreadySignedInPanel/AlreadySignedInPanel.component'
import { useLoginFlow } from './hooks/useLoginFlow'
import { useSsoRoute, type SsoRoutePhase, type EmailStepState } from './hooks/useSsoRoute'

// Moved here from SsoSection.component.tsx wholesale — the inline email step
// now lives only on this page. Keyed on EmailStepState's `error` union so an
// unhandled variant is a compile-time error, not a silent fallthrough.
const EMAIL_STEP_ERROR_MESSAGE_ID: Record<
  NonNullable<Extract<EmailStepState, { kind: 'open' }>['error']>,
  string
> = {
  domain_not_connected: 'login.sso.domainNotConnected',
  rate_limited: 'login.sso.rateLimited',
  lookup_failed: 'login.sso.lookupFailed',
}

// Stable placeholder — see LoginPage.container.tsx for why this can't be `[]` inline.
const EMPTY_OIDC_NODES: ReadonlyArray<UiNode> = []

// Dedicated page behind the /login "Sign in with SSO" button — the
// email-first SAML/OIDC step ORY and Box's SSO UX guides prescribe, rather
// than an inline reveal on /login. Reuses useLoginFlow/useSsoRoute wholesale;
// see LoginPage.container.tsx for the shared flow-fetch and hidden-form-submit
// mechanics this mirrors.
const SsoLoginPage = () => {
  const intl = useIntl()
  const location = useLocation()
  const flowId = new URLSearchParams(location.search).get('flow')
  const errorWelcome = intl.formatMessage({ id: 'login.error.welcome' })
  const state = useLoginFlow(flowId)

  const oidcNodes =
    state.kind === 'flow' ? state.flow.ui.nodes.filter((n) => n.group === 'oidc') : EMPTY_OIDC_NODES

  // Linking flow (OIDC email matches an existing password identity): terminal
  // so a remembered route can't auto-submit over it. Handed to /login below.
  const isLinkingFlow = state.kind === 'flow' && getFlowMessages(state.flow).some(isLinkingMessage)
  let phase: SsoRoutePhase
  if (state.kind === 'loading') {
    phase = 'loading'
  } else if (state.kind !== 'flow' || isLinkingFlow) {
    phase = 'terminal'
  } else {
    phase = 'flow'
  }
  // autoSubmitRemembered: this page has no primary button to click — a valid
  // remembered route submits itself the moment the flow confirms it.
  const sso = useSsoRoute(oidcNodes, phase, { autoSubmitRemembered: true })

  const [email, setEmail] = useState('')

  // Same hidden-form-submit mechanics as LoginPage.container.tsx — `.submit()`,
  // never `.requestSubmit()`.
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    if (sso.pendingProvider) {
      formRef.current?.submit()
    }
  }, [sso.pendingProvider])

  if (state.kind === 'loading') {
    return <AuthLoading />
  }

  // /login has the password field this page doesn't; `replace` avoids a Back loop.
  if (isLinkingFlow) {
    return <Navigate to={`/login${location.search}`} replace={true} />
  }

  if (state.kind === 'session') {
    const returnTo = new URLSearchParams(location.search).get('return_to')
    return <AlreadySignedInPanel session={state.session} returnTo={returnTo} />
  }

  if (state.kind === 'error') {
    return (
      <AuthError
        welcome={errorWelcome}
        message={state.message || intl.formatMessage({ id: 'login.expired' })}
      />
    )
  }

  const { flow } = state
  const defaultGroupNodes = flow.ui.nodes.filter((n) => n.group === 'default')
  const csrfAttrs = findInput(defaultGroupNodes, 'csrf_token')

  if (!csrfAttrs) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'login.error.missingFields' })}
      />
    )
  }
  if (!isSameOriginFormAction(flow.ui.action)) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'login.error.formActionUnexpectedOrigin' })}
      />
    )
  }

  const csrfValue = String(csrfAttrs.value ?? '')
  // 'closed' reads the same as 'open' with no error/pending — this page has
  // no link to open, it always shows the email step (or the auto-redirect
  // panel below).
  const emailStepError = sso.emailStep.kind === 'open' ? sso.emailStep.error : null
  const emailStepPending = sso.emailStep.kind === 'open' && sso.emailStep.pending

  const handleEmailSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (trimmed) {
      sso.submitEmailLookup(trimmed)
    }
  }

  return (
    <>
      <SplitAuthLayout
        heading={intl.formatMessage({ id: 'login.sso.page.heading' })}
        subheading=''
        dataTestId='sso-login-page'
        back={<BackToLogin />}
      >
        {sso.hasRememberedRoute ? (
          <Box
            data-testid='sso-auto-redirect'
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <InlineMessage variant='info'>
              <FormattedMessage id='login.sso.redirecting' />
            </InlineMessage>
            <Button
              variant='link'
              type='button'
              onClick={sso.useDifferentAccount}
              sx={{ textAlign: 'center' }}
            >
              <FormattedMessage id='login.sso.useDifferentAccount' />
            </Button>
          </Box>
        ) : (
          <Box
            component='form'
            data-testid='sso-email-form'
            onSubmit={handleEmailSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {emailStepError && (
              <InlineMessage variant='error'>
                <FormattedMessage id={EMAIL_STEP_ERROR_MESSAGE_ID[emailStepError]} />
              </InlineMessage>
            )}
            <TextField
              name='sso-email'
              type='text'
              icon={<MailIcon />}
              label={intl.formatMessage({ id: 'login.field.identifier.label' })}
              placeholder={intl.formatMessage({ id: 'login.field.identifier.placeholder' })}
              autoFocus={true}
              required={true}
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
            <Button
              variant='bordered'
              size='lg'
              block={true}
              type='submit'
              disabled={emailStepPending}
              sx={{ borderRadius: tokens.radiusMd }}
            >
              <FormattedMessage id={emailStepPending ? 'loading.default' : 'login.sso.continue'} />
            </Button>
          </Box>
        )}
      </SplitAuthLayout>

      {/* Programmatic-submit-only carrier to Kratos — never shown, so it sits
          outside SplitAuthLayout's gapped content column rather than adding an
          invisible flex item there. */}
      <Box
        component='form'
        ref={formRef}
        action={flow.ui.action}
        method={flow.ui.method}
        sx={{ display: 'none' }}
      >
        <input type='hidden' name='csrf_token' value={csrfValue} />
        {sso.pendingProvider && <input type='hidden' name='provider' value={sso.pendingProvider} />}
      </Box>
    </>
  )
}

export default SsoLoginPage
